import React from 'react';
import { ArrowRightLeft, Download, Loader2, ShieldAlert } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import SearchableSelect from '@/components/shared/SearchableSelect';
import { formatAmountInput, formatRial, parseAmountDigits } from '@/lib/formatMoney';
import { amountToPersianWordsToman } from '@/lib/rialWordsFa';
import {
  bsiInternetBankApi,
  type BsiIbAccountOption,
  type BsiIbTransferPreview,
} from '@/lib/banks/bsi/api';
import { downloadBsiReceipt } from '@/lib/banks/bsi/download';
import { isSessionExpiredMessage, type BsiFlashMessage } from './helpers';
import { clearBsiTransferPrefill, readBsiTransferPrefill } from '@/lib/api/advances';
import { cardToIbanApi } from '@/lib/api/cardToIban';
import { formatIranIbanDashed, isCompleteIranIban } from '@/lib/bsiTransferMode';
import BsiSystemShebaPicker from './BsiSystemShebaPicker';
import BsiDestinationInput from './BsiDestinationInput';
import BsiPendingSignBanner, { usePendingSignSummary } from './BsiPendingSignBanner';
import BsiTransferAwaitingSign from './BsiTransferAwaitingSign';

type Props = {
  sessionValid: boolean;
  onMessage: (message: BsiFlashMessage | null) => void;
  onSessionExpired: () => void;
};

type ChallengeType = 'sms' | 'signature_password' | string;
type UiStep = 'form' | 'confirm' | 'done';
type Channel = 'satna' | 'paya' | 'pol';

const channelLabel: Record<Channel, string> = {
  satna: 'ساتنا',
  paya: 'پایا',
  pol: 'پل',
};

const BsiInterbankTab: React.FC<Props> = ({ sessionValid, onMessage, onSessionExpired }) => {
  const [channel, setChannel] = React.useState<Channel>('satna');
  const [loadingForm, setLoadingForm] = React.useState(false);
  const [previewing, setPreviewing] = React.useState(false);
  const [confirming, setConfirming] = React.useState(false);
  const [step, setStep] = React.useState<UiStep>('form');

  const [draftKey, setDraftKey] = React.useState<string | null>(null);
  const [formPath, setFormPath] = React.useState<string | null>(null);
  const [sources, setSources] = React.useState<BsiIbAccountOption[]>([]);
  const [destinationIbans, setDestinationIbans] = React.useState<BsiIbAccountOption[]>([]);
  const [reasonCodes, setReasonCodes] = React.useState<BsiIbAccountOption[]>([]);

  const [sourceAccount, setSourceAccount] = React.useState('');
  const [destinationKnownAccount, setDestinationKnownAccount] = React.useState('');
  const [destinationIban, setDestinationIban] = React.useState('');
  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [corporateName, setCorporateName] = React.useState('');
  const [amount, setAmount] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [paymentId, setPaymentId] = React.useState('');
  const [reasonCode, setReasonCode] = React.useState('');
  const [reasonDesc, setReasonDesc] = React.useState('');

  const [preview, setPreview] = React.useState<BsiIbTransferPreview | null>(null);
  const [confirmDraftKey, setConfirmDraftKey] = React.useState<string | null>(null);
  const [challengeType, setChallengeType] = React.useState<ChallengeType>('sms');
  const [challengeLabel, setChallengeLabel] = React.useState('شناسه تأیید پیامک');
  const [authStep, setAuthStep] = React.useState(1);
  const [amountLocked, setAmountLocked] = React.useState(false);
  const [challenge, setChallenge] = React.useState('');
  const [confirmAmount, setConfirmAmount] = React.useState('');
  const [reference, setReference] = React.useState<string | null>(null);
  const [receiptKey, setReceiptKey] = React.useState<string | null>(null);
  const [ackIrreversible, setAckIrreversible] = React.useState(false);
  const [awaitingSign, setAwaitingSign] = React.useState(false);
  const [doneSignableCount, setDoneSignableCount] = React.useState(0);
  const pendingBeforeRef = React.useRef(0);
  const { signableCount, refresh: refreshPending } = usePendingSignSummary(sessionValid);

  const amountDigits = parseAmountDigits(amount);

  const loadForm = React.useCallback(
    async (nextChannel?: Channel) => {
      const useChannel = nextChannel || channel;
      setLoadingForm(true);
      onMessage(null);
      try {
        const res = await bsiInternetBankApi.interbankForm(useChannel);
        setChannel(res.data.channel || useChannel);
        setFormPath(res.data.form_path);
        setDraftKey(res.data.draft_key);
        setSources(res.data.source_accounts || []);
        setDestinationIbans(res.data.destination_ibans || []);
        setReasonCodes(res.data.reason_codes || []);
        setSourceAccount(res.data.source_accounts?.[0]?.value || '');
        setDestinationKnownAccount('');
        setDestinationIban('');
        setReasonCode(res.data.reason_codes?.[0]?.value || '');
        setReasonDesc('');
        setFirstName('');
        setLastName('');
        setCorporateName('');
        setPaymentId('');
        setStep('form');
        setPreview(null);
        setConfirmDraftKey(null);
        setChallenge('');
        setConfirmAmount('');
        setReference(null);
        setReceiptKey(null);
        setAmountLocked(false);
        setAwaitingSign(false);
        onMessage({ type: 'success', text: res.message || 'فرم بین‌بانکی آماده است.' });
        const prefill = readBsiTransferPrefill();
        if (prefill) {
          setAmount(String(prefill.amount ?? ''));
          if (prefill.description) setDescription(prefill.description);
          if (prefill.destination_iban) {
            setDestinationIban(formatIranIbanDashed(prefill.destination_iban));
            setDestinationKnownAccount('');
          }
          const fullName = (prefill.destination_name || '').trim();
          if (fullName) {
            const parts = fullName.split(/\s+/);
            setFirstName(parts[0] || '');
            setLastName(parts.slice(1).join(' ') || '');
          }
          clearBsiTransferPrefill();
        }
      } catch (e) {
        const text = e instanceof Error ? e.message : 'خطا در دریافت فرم بین‌بانکی';
        onMessage({ type: 'error', text });
        if (isSessionExpiredMessage(text)) onSessionExpired();
      } finally {
        setLoadingForm(false);
      }
    },
    [channel, onMessage, onSessionExpired],
  );

  React.useEffect(() => {
    if (sessionValid && sources.length === 0 && !loadingForm) void loadForm('satna');
  }, [sessionValid, sources.length, loadingForm, loadForm]);

  const resolveDestinationIban = async (): Promise<string | null> => {
    let iban = destinationIban.trim();
    if (destinationKnownAccount && !iban) {
      const known = destinationKnownAccount.split('--')[0] || destinationKnownAccount;
      iban = formatIranIbanDashed(known);
    }
    const cardDigits = iban.replace(/\D/g, '');
    if (cardDigits.length === 16 && !isCompleteIranIban(iban)) {
      const res = await cardToIbanApi.lookupFromCard(cardDigits);
      const converted = res.data?.iban?.trim();
      if (!converted) {
        throw new Error(res.message || 'شبا از کارت استخراج نشد.');
      }
      iban = formatIranIbanDashed(converted);
      setDestinationIban(iban);
      setDestinationKnownAccount('');
      const holder = (res.data?.account_holder_name ?? '').trim();
      if (holder) {
        const parts = holder.split(/\s+/);
        if (!firstName.trim()) setFirstName(parts[0] || '');
        if (!lastName.trim()) setLastName(parts.slice(1).join(' ') || '');
      }
    }
    if (!isCompleteIranIban(iban)) return null;
    return iban;
  };

  const handlePreview = async () => {
    if (!sourceAccount) {
      onMessage({ type: 'error', text: 'حساب مبدأ را انتخاب کنید.' });
      return;
    }
    if (!amountDigits) {
      onMessage({ type: 'error', text: 'مبلغ را وارد کنید.' });
      return;
    }
    if (!reasonCode) {
      onMessage({ type: 'error', text: 'علت انتقال را انتخاب کنید.' });
      return;
    }
    if (!destinationIban && !destinationKnownAccount) {
      onMessage({ type: 'error', text: 'شبا یا کارت مقصد را وارد یا از لیست انتخاب کنید.' });
      return;
    }
    setPreviewing(true);
    onMessage(null);
    try {
      const resolvedIban = await resolveDestinationIban();
      if (!resolvedIban) {
        onMessage({ type: 'error', text: 'شبا مقصد نامعتبر است یا هنوز از کارت تبدیل نشده.' });
        return;
      }
      const res = await bsiInternetBankApi.interbankPreview({
        channel,
        source_account: sourceAccount,
        destination_iban: resolvedIban,
        destination_known_account: destinationKnownAccount || undefined,
        amount: amountDigits,
        description: description.trim() || undefined,
        reason_code: reasonCode,
        reason_desc: reasonDesc.trim() || undefined,
        first_name: firstName.trim() || undefined,
        last_name: lastName.trim() || undefined,
        corporate_name: corporateName.trim() || undefined,
        payment_id: paymentId.trim() || undefined,
        draft_key: draftKey || undefined,
      });
      setPreview(res.data.preview);
      setConfirmDraftKey(res.data.confirm_draft_key);
      setChallengeType(res.data.challenge_type || 'sms');
      setChallengeLabel(res.data.challenge_label || 'شناسه تأیید پیامک');
      setAuthStep(res.data.step || 1);
      setChallenge('');
      setConfirmAmount(formatAmountInput(String(res.data.preview?.amount ?? amountDigits)));
      setAmountLocked(false);
      setStep('confirm');
      onMessage({ type: 'success', text: res.message || 'پیش‌نمایش آماده است.' });
    } catch (e) {
      const text = e instanceof Error ? e.message : 'پیش‌نمایش ناموفق بود.';
      onMessage({ type: 'error', text });
      if (isSessionExpiredMessage(text)) onSessionExpired();
    } finally {
      setPreviewing(false);
    }
  };

  const handleConfirm = async () => {
    if (!confirmDraftKey) return;
    if (!ackIrreversible) {
      onMessage({ type: 'error', text: 'برگشت‌ناپذیر بودن انتقال را تأیید کنید.' });
      return;
    }
    if (!challenge.trim()) {
      onMessage({
        type: 'error',
        text: challengeType === 'signature_password' ? 'رمز امضا را وارد کنید.' : 'شناسه پیامک را وارد کنید.',
      });
      return;
    }
    if (!amountLocked && !parseAmountDigits(confirmAmount)) {
      onMessage({ type: 'error', text: 'مبلغ تأیید را وارد کنید.' });
      return;
    }
    setConfirming(true);
    onMessage(null);
    pendingBeforeRef.current = signableCount;
    try {
      const res = await bsiInternetBankApi.localTransferConfirm({
        confirm_draft_key: confirmDraftKey,
        challenge,
        confirm_amount: amountLocked ? undefined : parseAmountDigits(confirmAmount),
      });
      if (res.data.needs_next_step) {
        setConfirmDraftKey(res.data.confirm_draft_key);
        setChallengeType(res.data.challenge_type || 'sms');
        setChallengeLabel(res.data.challenge_label || 'کد تأیید');
        setAuthStep(res.data.step || authStep + 1);
        setAmountLocked(true);
        setChallenge('');
        onMessage({ type: 'success', text: res.message || 'مرحله بعد را تکمیل کنید.' });
        return;
      }
      const refreshed = await refreshPending();
      const count = res.data.signable_count ?? refreshed ?? signableCount;
      const needsSign =
        Boolean(res.data.needs_pending_sign) ||
        count > pendingBeforeRef.current ||
        (!res.data.reference && count > 0);

      setReference(res.data.reference);
      setReceiptKey(res.data.receipt_key);
      setDoneSignableCount(count);
      setAwaitingSign(needsSign);
      setStep('done');
      onMessage({
        type: 'success',
        text: needsSign
          ? res.message || 'انتقال ثبت شد و در انتظار امضاست.'
          : res.message || 'درخواست به بانک ارسال شد.',
      });
    } catch (e) {
      const text = e instanceof Error ? e.message : 'تأیید ناموفق بود.';
      onMessage({ type: 'error', text });
      if (isSessionExpiredMessage(text)) onSessionExpired();
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Card className="lg:col-span-3">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ArrowRightLeft className="h-4 w-4" />
          انتقال بین‌بانکی
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!sessionValid ? (
          <p className="text-sm text-amber-700">برای انتقال بین‌بانکی ابتدا وارد اینترنت‌بانک شوید.</p>
        ) : (
          <>
            {sessionValid ? <BsiPendingSignBanner signableCount={signableCount} /> : null}
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
              <div className="mb-1 flex items-center gap-1 font-medium">
                <ShieldAlert className="h-3.5 w-3.5" />
                هشدار
              </div>
              ساتنا، پایا و پل بعد از ثبت معمولاً در بخش «قابل امضا» می‌مانند تا امضای نهایی انجام شود.
            </div>

            <div className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900">
              <p className="mb-1 font-medium">سقف‌ها و محدودیت‌ها (بانک)</p>
              {channel === 'satna' && (
                <ul className="list-disc space-y-0.5 pr-4">
                  <li>ساعات کاری: شنبه تا پنج‌شنبه ۷:۰۰ تا ۱۳:۰۰</li>
                </ul>
              )}
              {channel === 'pol' && (
                <ul className="list-disc space-y-0.5 pr-4">
                  <li>سقف هر تراکنش / روزانه پل: {formatRial(1_000_000_000)} ریال</li>
                  <li>سقف برداشت روزانه ترکیبی: {formatRial(10_000_000_000)} ریال</li>
                  <li>کارمزد: ۰٫۰۲٪ (حداقل ۵٬۰۰۰ ریال)</li>
                </ul>
              )}
              {channel === 'paya' && (
                <ul className="list-disc space-y-0.5 pr-4">
                  <li>چرخه‌های پایا: ۳:۴۵، ۹:۴۵، ۱۲:۴۵، ۱۸:۴۵</li>
                </ul>
              )}
            </div>
            {formPath && <p className="text-xs text-slate-500">مسیر فرم بانک: <span className="font-mono">{formPath}</span></p>}

            {step === 'form' && (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="space-y-2 md:col-span-2">
                  <Label>کانال</Label>
                  <SearchableSelect
                    value={channel}
                    onChange={(v) => {
                      const next = (v as Channel) || 'satna';
                      setChannel(next);
                      void loadForm(next);
                    }}
                    options={[
                      { value: 'satna', label: 'ساتنا' },
                      { value: 'paya', label: 'پایا' },
                      { value: 'pol', label: 'پل' },
                    ]}
                    placeholder="انتخاب کانال"
                  />
                </div>
                <div className="space-y-2">
                  <Label>حساب مبدأ</Label>
                  <SearchableSelect
                    value={sourceAccount}
                    onChange={setSourceAccount}
                    options={sources.map((a) => ({ value: a.value, label: a.label || a.value }))}
                    placeholder="انتخاب مبدأ"
                    disabled={loadingForm}
                  />
                </div>
                <div className="space-y-2">
                  <Label>شبا مقصد از لیست بانک</Label>
                  <SearchableSelect
                    value={destinationKnownAccount}
                    onChange={(v) => {
                      setDestinationKnownAccount(v);
                      if (v) {
                        setDestinationIban(formatIranIbanDashed(v.split('--')[0] || v));
                        setCorporateName('');
                      }
                    }}
                    options={[
                      { value: '', label: 'انتخاب نشده' },
                      ...destinationIbans.map((a) => ({ value: a.value, label: a.label || a.value })),
                    ]}
                    placeholder="مقاصد ذخیره‌شده در بانک"
                    disabled={loadingForm}
                  />
                </div>
                <BsiSystemShebaPicker
                  disabled={loadingForm}
                  onSelect={(selection) => {
                    setDestinationKnownAccount('');
                    setDestinationIban(selection.iban);
                    setFirstName(selection.firstName);
                    setLastName(selection.lastName);
                    setCorporateName('');
                  }}
                />
                <div className="md:col-span-2">
                  <BsiDestinationInput
                    value={destinationIban}
                    disabled={loadingForm || previewing}
                    onChange={(v) => {
                      setDestinationIban(v);
                      setDestinationKnownAccount('');
                    }}
                    onHolderResolved={({ firstName: fn, lastName: ln }) => {
                      if (fn && !firstName.trim()) setFirstName(fn);
                      if (ln && !lastName.trim()) setLastName(ln);
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label>نام</Label>
                  <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="نام صاحب حساب" />
                </div>
                <div className="space-y-2">
                  <Label>نام خانوادگی</Label>
                  <Input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="نام خانوادگی" />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>نام شرکت/حقوقی</Label>
                  <Input value={corporateName} onChange={(e) => setCorporateName(e.target.value)} placeholder="در صورت حقوقی بودن" />
                </div>
                <div className="space-y-2">
                  <Label>مبلغ (ریال)</Label>
                  <Input value={amount} onChange={(e) => setAmount(formatAmountInput(e.target.value))} placeholder="1,000,000" dir="ltr" className="font-mono" />
                  <p className="text-xs text-slate-600">{amountDigits ? amountToPersianWordsToman(Number(amountDigits)) : ' '}</p>
                </div>
                <div className="space-y-2">
                  <Label>شناسه پرداخت</Label>
                  <Input value={paymentId} onChange={(e) => setPaymentId(e.target.value)} placeholder="اختیاری" />
                </div>
                <div className="space-y-2">
                  <Label>علت انتقال</Label>
                  <SearchableSelect
                    value={reasonCode}
                    onChange={setReasonCode}
                    options={reasonCodes.map((r) => ({ value: r.value, label: r.label || r.value }))}
                    placeholder="انتخاب علت"
                  />
                </div>
                <div className="space-y-2">
                  <Label>شرح علت</Label>
                  <Input value={reasonDesc} onChange={(e) => setReasonDesc(e.target.value.slice(0, 25))} maxLength={25} placeholder="حداکثر ۲۵ کاراکتر" />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>شرح</Label>
                  <Textarea value={description} onChange={(e) => setDescription(e.target.value)} className="min-h-[88px]" />
                </div>
                <div className="flex flex-wrap gap-2 md:col-span-2">
                  <Button type="button" variant="outline" onClick={() => void loadForm(channel)} disabled={loadingForm}>
                    {loadingForm ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    <span className="mr-1">بروزرسانی فرم</span>
                  </Button>
                  <Button type="button" onClick={() => void handlePreview()} disabled={previewing || loadingForm}>
                    {previewing ? <Loader2 className="ml-1 h-4 w-4 animate-spin" /> : null}
                    پیش‌نمایش {channelLabel[channel]}
                  </Button>
                </div>
              </div>
            )}

            {step === 'confirm' && preview && (
              <div className="space-y-4">
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  مرحله {authStep} — {challengeType === 'signature_password' ? 'رمز امضا' : 'شناسه تأیید پیامک'}
                </div>
                <div className="rounded-md border bg-slate-50 px-3 py-3 text-sm space-y-1">
                  <p>کانال: {channelLabel[channel]}</p>
                  <p>از: <span className="font-mono">{preview.source_account}</span></p>
                  <p>به: <span className="font-mono">{preview.destination_account}</span></p>
                  <p>مبلغ: <span className="font-mono font-semibold">{formatRial(preview.amount)}</span> ریال</p>
                </div>
                {!amountLocked && (
                  <div className="space-y-2">
                    <Label>تأیید مبلغ</Label>
                    <Input value={confirmAmount} onChange={(e) => setConfirmAmount(formatAmountInput(e.target.value))} dir="ltr" className="font-mono" />
                  </div>
                )}
                <div className="space-y-2">
                  <Label>{challengeLabel}</Label>
                  <Input
                    type={challengeType === 'signature_password' ? 'password' : 'text'}
                    value={challenge}
                    onChange={(e) => setChallenge(e.target.value)}
                    inputMode={challengeType === 'sms' ? 'numeric' : undefined}
                  />
                </div>
                <label className="flex items-start gap-2 text-sm text-rose-800">
                  <input type="checkbox" className="mt-1" checked={ackIrreversible} onChange={(e) => setAckIrreversible(e.target.checked)} />
                  <span>جزئیات را بررسی کردم و ادامه را تأیید می‌کنم.</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="outline" onClick={() => setStep('form')} disabled={confirming}>
                    بازگشت
                  </Button>
                  <Button type="button" variant="destructive" onClick={() => void handleConfirm()} disabled={confirming}>
                    {confirming ? <Loader2 className="ml-1 h-4 w-4 animate-spin" /> : null}
                    تأیید
                  </Button>
                </div>
              </div>
            )}

            {step === 'done' && awaitingSign && (
              <BsiTransferAwaitingSign
                title={`ثبت ${channelLabel[channel]} — در انتظار امضا`}
                reference={reference}
                signableCount={doneSignableCount || signableCount}
                onNewTransfer={() => {
                  setStep('form');
                  setAwaitingSign(false);
                  setReceiptKey(null);
                  setReference(null);
                }}
              />
            )}

            {step === 'done' && !awaitingSign && (
              <div className="space-y-3 text-sm">
                <p className="text-emerald-700">درخواست {channelLabel[channel]} ارسال شد.</p>
                {reference && <p>پیگیری: <span className="font-mono font-semibold">{reference}</span></p>}
                <div className="flex flex-wrap gap-2">
                  {receiptKey && (
                    <>
                      <Button type="button" variant="outline" onClick={() => void downloadBsiReceipt(receiptKey, 'html').catch((e) => onMessage({ type: 'error', text: e instanceof Error ? e.message : 'دانلود ناموفق' }))}>
                        <Download className="ml-1 h-4 w-4" />
                        HTML
                      </Button>
                      <Button type="button" variant="outline" onClick={() => void downloadBsiReceipt(receiptKey, 'pdf').catch((e) => onMessage({ type: 'error', text: e instanceof Error ? e.message : 'دانلود ناموفق' }))}>
                        <Download className="ml-1 h-4 w-4" />
                        PDF
                      </Button>
                      <Button type="button" variant="outline" onClick={() => void downloadBsiReceipt(receiptKey, 'excel').catch((e) => onMessage({ type: 'error', text: e instanceof Error ? e.message : 'دانلود ناموفق' }))}>
                        <Download className="ml-1 h-4 w-4" />
                        اکسل
                      </Button>
                    </>
                  )}
                  <Button type="button" variant="outline" onClick={() => { setStep('form'); setAwaitingSign(false); setReceiptKey(null); setReference(null); }}>تراکنش جدید</Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default BsiInterbankTab;
